<template>
  <div class="">
    <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
      <div>
        <h2 class="text-xl font-semibold text-gray-800">List of system-wide tags</h2>
      </div>
      <button
        @click="addingNewItem"
        class="flex items-center gap-2 bg-blue-800 hover:bg-blue-900 text-white font-semibold px-3 py-2 rounded-lg shadow transition"
      >
        <span class="material-icons text-base">add</span>
        Add Tag
      </button>
    </div>
    <!-- Search Bar -->
    <div class="flex flex-wrap items-center justify-between gap-2 mb-2">
      <div class="flex items-center gap-2">
        <vs-input style="width: 100%; border-radius: 50%" label-placeholder="Tag name/language" @keypress.enter="searchData()" v-model="search" />
        <button
          @click="searchData"
          class="flex items-center gap-2 bg-blue-800 hover:bg-blue-900 text-white font-semibold px-3 py-2 rounded-lg shadow transition"
        >
          <span class="material-icons text-base">search</span>
          Search
        </button>
      </div>
      <div class="flex items-center gap-2">
        <!-- lang filter -->
        <vs-select v-model="langFilter" @change="fetchAllTag" class="w-28" :color="'#6366f1'">
          <vs-select-item value="all" text="Language" disabled />
          <vs-select-item v-for="lang in languages" :key="lang.culture" :value="lang.culture" :text="lang.culture" />
        </vs-select>
        <!--  refresh button -->
        <button
          @click="resetData()"
          class="flex items-center gap-1 bg-yellow-500 hover:bg-yellow-600 text-white px-4 py-2 rounded-lg font-medium transition"
        >
          <span class="material-icons text-base">refresh</span> Reset
        </button>
      </div>
    </div>
    <!-- Table -->
    <div class="overflow-x-auto rounded-xl border border-gray-200 bg-white pb-2">
      <table class="min-w-full bg-white rounded-xl">
        <thead class="border-b">
          <tr>
            <th class="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">#</th>
            <th class="px-4 py-3 text-xs font-semibold text-gray-500 uppercase">Name</th>
            <th class="px-4 py-3 text-xs text-center font-semibold text-gray-500 uppercase">Language</th>
            <th class="px-4 py-3"></th>
          </tr>
          <tr
            v-for="(item, idx) in all"
            v-if="all && all.length > 0"
            :key="item._id"
            class="border-b hover:bg-gray-100 transition"
            :title="`Click to view posts under ${item.name} tag`"
            @click="viewAllPosts(item)"
          >
            <td class="px-4 py-3">{{ idx + 1 + (currentPage - 1) * limit }}</td>
            <td class="td">{{ item.name }}</td>
            <td
              class="px-4 py-3 text-center"
              :title="item.missingLanguages.length > 0 ? `Missing: ${item.missingLanguages.join(', ')}` : 'All languages available'"
            >
              <div class="flex items-center justify-center gap-2">
                <span class="text-gray-500 text-lg">{{ item.language }}</span>
                <span
                  v-if="item.languageCount && item.missingLanguages && item.language === 'EN'"
                  :class="[
                    'inline-flex items-center px-2 py-1 rounded-full font-semibold cursor-pointer',
                    item.languageCount - item.missingLanguages.length !== 11 ? 'bg-red-900 text-white' : 'bg-blue-900 text-white',
                  ]"
                >
                  {{ item.languageCount - item.missingLanguages.length }}/{{ item.languageCount }}
                </span>
                <span v-if="item.language === 'EN'" class="material-icons text-gray-400 cursor-pointer" style="font-size: 20px">info</span>
              </div>
            </td>
            <td class="px-4 py-3">
              <vs-dropdown>
                <svg class="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <circle cx="12" cy="5" r="1.5" />
                  <circle cx="12" cy="12" r="1.5" />
                  <circle cx="12" cy="19" r="1.5" />
                </svg>
                <vs-dropdown-menu>
                  <vs-dropdown-item @click="editingItem(item)"> <vs-icon icon="create" class="align-right" /> Edit </vs-dropdown-item>
                  <vs-dropdown-item
                    @click="
                      deleteTagPopup = true;
                      itemToDelete = item;
                    "
                  >
                    <vs-icon icon="delete_sweep" /> Delete
                  </vs-dropdown-item>
                  <!-- <vs-dropdown-item @click="viewAllPosts(item)"><vs-icon icon="visibility" class="align-right" /> View posts </vs-dropdown-item> -->
                </vs-dropdown-menu>
              </vs-dropdown>
            </td>
          </tr>
        </thead>
      </table>
      <div v-if="!all || all.length === 0" class="p-4 text-center text-gray-500">No tags found.</div>
    </div>
    <!-- Delete Confirmation Popup -->
    <vs-popup title="Delete Tag" :active.sync="deleteTagPopup">
      <p class="mb-4 text-lg text-gray-700">Are you sure you want to delete this tag?</p>
      <div class="flex justify-end gap-2">
        <vs-button color="warning" @click="confirmDeleteTag">Delete</vs-button>
        <vs-button color="primary" @click="deleteTagPopup = false">Cancel</vs-button>
      </div>
    </vs-popup>
    <!-- Add Category Popup -->
    <vs-popup :title="isEdit ? 'Edit Tag' : 'Add New Tag'" :active.sync="defaultalert">
      <form @submit.prevent="isEdit ? handleUpdateCategory() : handleSaveCategory()">
        <!-- language filter -->
        <div class="mb-2 px-2 flex justify-between">
          <div class="w-1/4" v-if="isEdit">
            <label class="block text-sm font-semibold mb-2">Language</label>
            <vs-select v-model="tag.language" @change="refetchUsingLangugage($event, tag)" class="w-full" :color="'#6366f1'" :disabled="showLoader">
              <vs-select-item value="" text="Select Language" disabled />
              <vs-select-item v-for="lang in languages" :key="lang.culture" :value="lang.culture" :text="lang.culture" />
            </vs-select>
          </div>
          <div v-if="isEdit && tag.language === 'EN'" :title="'Toggle to update existing translations'">
            <label class="block text-sm font-semibold mb-2">Update Translations</label>
            <toggle-pill v-model="tag.translate" :disabled="showLoader" />
          </div>
        </div>
        <div class="px-2">
          <label class="block text-sm font-semibold mb-2">Name</label>
          <vs-input v-model="tag.name" placeholder="Keep short and simple" class="w-full" required :disabled="showLoader" />
          <!-- independent language to translate -->
          <div class="pt-2" v-if="!isEdit && tag.language === 'EN'">
            <label class="block text-sm font-semibold mb-2">Choose Languages to Translate</label>
            <div class="grid grid-cols-7 gap-2">
              <vs-checkbox v-model="selectAllLang" @change="toggleSelectAll" :disabled="showLoader" class="z-0">All</vs-checkbox>
              <div v-for="lang in languages" :key="lang.culture">
                <vs-checkbox
                  :value="tag.languagesToTranslate.includes(lang.culture)"
                  @change="toggleLanguage(lang.culture)"
                  :disabled="lang.culture === 'EN' || showLoader"
                  class="z-0"
                >
                  {{ lang.culture }}
                </vs-checkbox>
              </div>
            </div>
          </div>
          <!-- add missing languages -->
          <div v-if="isEdit && tag.language === 'EN' && tag.missingLanguages && tag.missingLanguages.length > 0">
            <label class="block text-sm font-semibold pt-1 mb-2">Add missing translations</label>
            <vs-checkbox v-model="tag.translateMissing" :disabled="showLoader">Add missing translations</vs-checkbox>
            <div v-if="tag.translateMissing" class="grid grid-cols-7 gap-2 mt-3">
              <vs-checkbox v-model="selectAllEdit" @change="toggleSelectAllEdit" :disabled="showLoader" class="z-0">All</vs-checkbox>
              <div v-for="lang in tag.missingLanguages" :key="lang">
                <vs-checkbox
                  :value="tag.languagesToTranslate.includes(lang) && lang !== 'EN'"
                  :disabled="lang === 'EN' || showLoader"
                  @change="toggleLanguage(lang)"
                  class="z-0"
                >
                  {{ lang }}
                </vs-checkbox>
              </div>
            </div>
          </div>

          <!-- Loader message -->
          <div v-if="showLoader" class="mb-4 text-blue-700">Translations are working in background...</div>

          <div class="flex flex-col md:flex-row gap-2 mt-8">
            <vs-button
              v-if="isEdit"
              :loading="showLoader"
              color="primary"
              type="filled"
              class="flex-1"
              @click="handleUpdateCategory"
              :disabled="showLoader"
            >
              {{ tag.translateMissing ? "Update And Translate Missing" : tag.translate ? "Update Translations and Save" : "Update Current" }}
            </vs-button>
            <vs-button
              v-if="!isEdit"
              :loading="showLoader"
              color="primary"
              type="filled"
              class="flex-1"
              @click="handleSaveCategory"
              :disabled="showLoader"
            >
              {{ tag.languagesToTranslate && tag.languagesToTranslate.length > 1 ? "Save and Translate" : "Save English" }}
            </vs-button>
            <!-- save and translate toggle
            <div class="flex flex-col" v-if="!isEdit" style="width: 50px">
              <label class="block text-xs font-semibold">Translate?</label>
              <toggle-pill v-model="tag.translate" :disabled="showLoader" />
            </div> -->
          </div>
        </div>
      </form>
    </vs-popup>
  </div>
</template>

<script>
import api from "@/rest.js";
import { API } from "@/common/constants";
import TogglePill from "../components/Common/TogglePill.vue";

export default {
  name: "Tags",
  components: {
    TogglePill,
  },
  data() {
    return {
      languages: [],
      all: [],
      currentPage: 1,
      totalPages: 1,
      limit: 25,
      deleteTagPopup: false,
      defaultalert: false,
      isEdit: true,
      search: "",
      langFilter: "EN",
      tag: {
        name: "",
        language: "",
        ref: "",
        translate: false,
        languagesToTranslate: [],
        translateMissing: false,
      },
      selectAllLang: false,
      selectAllEdit: false,
      itemToDelete: "",
      itemToUpdate: "",
      showLoader: false,
    };
  },
  methods: {
    toggleSelectAll() {
      if (this.selectAllLang) {
        this.tag.languagesToTranslate = this.languages.map((lang) => lang.culture);
      } else {
        this.tag.languagesToTranslate = ["EN"];
      }
    },

    toggleSelectAllEdit() {
      if (this.selectAllEdit) {
        this.tag.languagesToTranslate = this.tag.missingLanguages;
      } else {
        this.tag.languagesToTranslate = ["EN"];
      }
    },

    toggleLanguage(culture) {
      const selectedLanguages = this.tag.languagesToTranslate;

      if (selectedLanguages.includes(culture)) {
        this.tag.languagesToTranslate = selectedLanguages.filter((lang) => lang !== culture);
      } else {
        this.tag.languagesToTranslate = [...selectedLanguages, culture];
      }

      const allLanguagesSelected = this.languages.every((lang) => this.tag.languagesToTranslate.includes(lang.culture));
      this.selectAllLang = allLanguagesSelected;
      if (this.selectAllEdit) {
        const allLanguagesSelected = this.tag.missingLanguages.every((lang) => this.tag.languagesToTranslate.includes(lang));
        this.selectAllEdit = allLanguagesSelected;
      }
    },

    addingNewItem() {
      this.$data.isEdit = false;
      this.$data.tag = {
        name: "",
        language: "EN",
        translate: false,
        languagesToTranslate: ["EN"],
        translateMissing: false,
      };
      this.selectAllLang = false;
      this.selectAllEdit = false;
      this.$data.defaultalert = true;
    },

    editingItem(i) {
      this.$data.tag = Object.assign(this.$data.tag, i);
      this.$data.tag.languagesToTranslate = ["EN"];
      this.selectAllLang = false;
      this.selectAllEdit = false;
      this.$data.isEdit = true;
      this.$data.defaultalert = true;
    },

    resetData() {
      this.fetchAllTag();
      this.$data.search = "";
      this.$data.langFilter = "EN";
    },

    viewAllPosts(i) {
      this.$router.push("/post/tag/?tagName=" + i.name + "&tagLang=" + i.language);
    },

    async handleSaveCategory() {
      this.$vs.loading();
      if (this.$data.tag.translate) this.showLoader = true;
      try {
        if (this.$data.tag.language === "" || this.$data.tag.name === "") {
          this.$vs.notify({
            title: "Error",
            text: "Please fill all fields",
            color: "danger",
            position: "top-right",
          });
          return;
        }

        let data = { ...this.$data.tag };
        delete data.translate;

        let r;
        if (this.$data.tag.languagesToTranslate.length > 1) r = await api.post(API.C_AND_T_TAG, data);
        else r = await api.post(API.INSERT_TAG, data);

        if (r.status === 200) {
          this.$data.isEdit = false;
          this.$data.itemToUpdate = "";
          this.$data.defaultalert = false;
          this.$data.tag = {};
          this.$vs.notify({
            title: "Success",
            text: "Saved Successfully",
            color: "success",
            position: "top-right",
          });
          await this.fetchAllTag();
        }
      } catch (error) {
        console.error(error);
        this.$vs.notify({
          text: "Error saving tags",
          color: "danger",
          position: "top-right",
        });
      } finally {
        this.$vs.loading.close();
        this.showLoader = false;
      }
    },

    async handleUpdateCategory() {
      this.$vs.loading();
      if (this.$data.tag.translate) this.showLoader = true;

      try {
        if (this.$data.tag.language === "" || this.$data.tag.name === "") {
          this.$vs.notify({
            title: "Error",
            text: "Please fill all fields",
            color: "danger",
            position: "top-right",
          });
          return;
        }

        let data = { ...this.$data.tag };
        delete data.translate;

        let r;
        if (this.$data.tag.languagesToTranslate.length > 1) r = await api.put(`${API.U_AND_T_TAG}/${this.$data.tag._id}`, data);
        else r = await api.put(`${API.UPDATE_TAG}/${this.$data.tag._id}`, data);

        if (r.status === 200) {
          this.$data.isEdit = false;
          this.$data.itemToUpdate = "";
          this.$data.defaultalert = false;
          this.$data.tag = {};
          await this.fetchAllTag();
          this.$vs.notify({
            title: "Success",
            text: "Update successful",
            color: "success",
            position: "top-right",
          });
        }
      } catch (error) {
        console.error(error);
        this.$vs.notify({
          text: "Error saving tags",
          color: "danger",
          position: "top-right",
        });
      } finally {
        this.$vs.loading.close();
        this.showLoader = false;
      }
    },

    async confirmDeleteTag() {
      this.$vs.loading();

      try {
        const r = await api.delete(`${API.DELETE_TAG}/${this.$data.itemToDelete._id}`, {});
        if (r.status === 200) {
          this.$vs.notify({
            text: "Deleted Successfully",
            color: "success",
            position: "top-right",
          });
          this.$data.deleteTagPopup = false;
          await this.fetchAllTag();
        }
      } catch (error) {
        console.error(error);
        this.$vs.notify({
          text: "Error deleting tag",
          color: "danger",
          position: "top-right",
        });
      } finally {
        this.$vs.loading.close();
      }
    },

    async fetchAllTag() {
      this.$vs.loading();
      try {
        const r = await api.get(`${API.GET_TAG}?language=${this.$data.langFilter}`, {});
        if (r.status === 200) {
          if (typeof r.data === "string") this.$data.all = [];
          else this.$data.all = r.data;
        }
      } catch (error) {
        console.error(error);
        this.$vs.notify({
          text: "Error fetching tags",
          color: "danger",
          position: "top-right",
        });
      } finally {
        this.$vs.loading.close();
      }
    },

    async fetchAllLanguages() {
      this.$vs.loading();
      try {
        const r = await await api.get(API.GET_LANG, {});
        this.languages = r.data;
      } catch (error) {
        this.$vs.notify({
          text: "Error fetching languages",
          color: "danger",
          position: "top-right",
        });
      } finally {
        this.$vs.loading.close();
      }
    },

    async searchData() {
      this.$vs.loading();
      try {
        const r = await api.get(`${API.SEARCH_TAG}?search=${this.$data.search}`, {});

        if (r.status === 200) this.$data.all = r.data;
      } catch (error) {
        console.error(error);
        this.$vs.notify({
          title: "Error",
          text: "Error",
          color: "danger",
          position: "top-right",
        });
      } finally {
        this.$vs.loading.close();
      }
    },

    async refetchUsingLangugage(event, item) {
      try {
        if (!event || !this.isEdit) return;

        let id = item.ref || item._id;
        this.$vs.loading();

        const r = await api.get(`${API.GET_SINGLE_TAG}/${id}?language=${event}`);

        if (r.status === 200) this.$data.tag = Object.assign({}, r.data);
      } catch (error) {
        console.error(error);
        this.$vs.notify({
          title: "Error re-fetching",
          text: "Error",
          color: "danger",
          position: "top-right",
        });
      } finally {
        this.$vs.loading.close();
      }
    },
  },
  async mounted() {
    await Promise.all([this.fetchAllTag(), this.fetchAllLanguages()]);
  },
};
</script>

<style scoped>
.material-icons {
  font-family: "Material Icons";
  font-style: normal;
  font-weight: normal;
  font-size: 20px;
  line-height: 1;
  letter-spacing: normal;
  text-transform: none;
  display: inline-block;
  white-space: nowrap;
  direction: ltr;
  -webkit-font-smoothing: antialiased;
}
</style>
